<!--
  Email List Field — a combobox for editing a list of email addresses, with a read-only chip
  list variant. Entries are validated on change and on blur; the model is only emitted once
  every address parses, and invalid chips are highlighted in the error color.
-->
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

/** A combobox selection. `return-object` is on, so the slot hands back the wrapped item. */
interface EmailItem {
  value: string;
}

const {
  modelValue = [],
  label,
  chipColor = 'secondary',
  tooltipText,
  readOnly = false,
  stacked = true,
  icon = null
} = defineProps<{
  /** Selected email addresses. */
  modelValue?: string[];
  /** Field label. Also enables the help tooltip beside it. */
  label?: string;
  /** Color for chips holding a valid address. */
  chipColor?: string;
  /** Overrides the default help tooltip copy. */
  tooltipText?: string;
  /** Render a static chip list instead of the combobox. */
  readOnly?: boolean;
  /** Stack the selections vertically. */
  stacked?: boolean;
  /** Icon to prepend to the field. */
  icon?: string | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string[]];
}>();

const { t } = useI18n();

/** Local mirror of the model, deduplicated. Validation runs against this list. */
const emails = ref<string[]>([]);
const invalidEmails = ref<string[]>([]);
const errorMessage = ref<string | null>(null);
const hideNoData = ref(false);

const editingItem = ref<EmailItem | null>(null);
const editingValue = ref('');
const editingItemDialog = computed(() => Boolean(editingItem.value));

const translatedTooltip = computed(
  () =>
    tooltipText ??
    t(
      'forms.elements.selects.emailList.tooltips.listEmailAddressesThatWillReceiveNotificationsAboutAppointment'
    )
);

// TODO we have created a task to move this to a generic location
function isValidEmail(email: string): boolean {
  return /^(?<localPart>(?<unquotedLocal>[^<>()[\]\\.,;:\s@']+(?<dotSegment>\.[^<>()\\[\]\\.,;:\s@']+)*)|(?<quotedLocal>'.+'))@(?<domain>(?<ipLiteral>\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(?<domainName>(?<domainLabel>[a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/u.test(
    email
  );
}

/** Validates the current list, emitting it only when every address parses. */
function validate(): boolean {
  invalidEmails.value = emails.value.filter(email => !isValidEmail(email));

  const invalidCount = invalidEmails.value.length;
  errorMessage.value = invalidCount
    ? `There ${invalidCount === 1 ? 'is' : 'are'} ${invalidCount} invalid email${
        invalidCount === 1 ? '' : 's'
      }.`
    : null;

  if (!invalidCount) {
    emit('update:modelValue', emails.value);
  }

  return !invalidCount;
}

function setEmails(): void {
  emails.value = [...new Set(modelValue ?? [])];
}

function onComboboxUpdate(value: string[]): void {
  emails.value = value;
  validate();
}

function removeChip(value: string): void {
  if (emails.value.includes(value)) {
    emit(
      'update:modelValue',
      emails.value.filter(email => email !== value)
    );
  }
}

function openEdit(item: EmailItem): void {
  editingItem.value = item;
  editingValue.value = item.value;
}

function closeEdit(): void {
  editingItem.value = null;
  editingValue.value = '';
}

function handleEmailSave(): void {
  if (!editingItem.value) {
    return;
  }
  emails.value = emails.value.map(email =>
    email === editingItem.value?.value ? editingValue.value : email
  );
  validate();
  closeEdit();
}

watch(
  () => modelValue,
  value => {
    setEmails();
    hideNoData.value = Boolean(value?.length);
  }
);

// Deliberately not an `immediate` watcher: this mirrors the original `mounted()` hook, which
// seeded the mirror but left `hideNoData` false on first render even for a non-empty list.
setEmails();

// Parents hold a ref to this component to gate form submission on `validate()`, and read
// `errorMessage` to enable their save buttons.
defineExpose({ validate, isValidEmail, errorMessage });
</script>

<template>
  <div class="flex-grow-1">
    <template v-if="readOnly">
      <div class="mt-3 font-size-x-small">
        {{ t('forms.elements.selects.emailList.labels.emailCcs') }}
      </div>
      <div class="d-flex flex-wrap gap-2 mt-3">
        <v-chip v-for="email in modelValue" :key="`email-${email}`" color="dark-gray">
          <span class="email-value">{{ email }}</span>
        </v-chip>
      </div>
    </template>

    <v-combobox
      v-else
      chips
      clear-on-select
      multiple
      no-filter
      persistent-placeholder
      return-object
      hide-details="auto"
      variant="outlined"
      v-bind="$attrs"
      :class="{ 'label-with-tooltip': label, 'stack-selections': stacked }"
      :data-testid="$attrs['test-id']"
      :delimiters="[',', ' ']"
      :error-messages="errorMessage"
      :hide-no-data="hideNoData"
      :label="label"
      :menu-props="{ openOnFocus: true, openOnClick: false }"
      :model-value="modelValue"
      :placeholder="t('forms.elements.selects.emailList.placeholders.emailSubscribers')"
      :prepend-icon="icon"
      @blur="validate"
      @update:model-value="onComboboxUpdate">
      <template #chip="{ item }">
        <v-chip
          :key="item.value"
          class="email-chip"
          label
          closable
          :color="invalidEmails.includes(item.value) ? 'error' : chipColor"
          @click.stop="openEdit(item)"
          @click:close.stop="removeChip(item.value)">
          <span class="email-value">{{ item.value }}</span>
        </v-chip>
      </template>

      <template v-if="label" #label>
        <div class="d-flex align-center">
          {{ label }}
          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <v-icon v-bind="tooltipProps" class="ml-1 text-grey-darken-2" size="small">
                mdi-help-circle
              </v-icon>
            </template>
            {{ translatedTooltip }}
          </v-tooltip>
        </div>
      </template>

      <template v-if="!hideNoData" #no-data>
        <v-list-item class="input-instructions">
          <v-list-item-title>
            <i18n-t
              keypath="forms.elements.selects.emailList.messages.pressEnterOrTabToAddEmail"
              tag="span"
              scope="global">
              <template #enterKey>
                <kbd>{{ t('forms.common.keys.enter') }}</kbd>
              </template>
              <template #tabKey>
                <kbd>{{ t('forms.common.keys.tab') }}</kbd>
              </template>
            </i18n-t>
          </v-list-item-title>
        </v-list-item>
      </template>
    </v-combobox>

    <v-dialog v-model="editingItemDialog" max-width="320">
      <v-card>
        <v-card-title>Update Email</v-card-title>

        <v-card-text>
          <v-text-field
            v-model="editingValue"
            label="Edit"
            single-line
            autofocus
            data-testid="email-list-edit-input"></v-text-field>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="error" variant="text" @click="closeEdit">Nevermind</v-btn>
          <v-btn
            color="primary"
            variant="text"
            :disabled="!editingValue || editingValue === editingItem.value"
            @click="handleEmailSave">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
.email-chip {
  margin-bottom: 0 !important;
}
</style>
